import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { FiSearch, FiFilter, FiDownload, FiUsers, FiCpu, FiCalendar, FiMapPin, FiBriefcase, FiDollarSign, FiAlertTriangle, FiX, FiHelpCircle } from 'react-icons/fi';
import GradientButton from '../components/ui/GradientButton';
import GradientText from '../components/ui/GradientText';
import GlassCard from '../components/ui/GlassCard';
import Card3D from '../components/ui/Card3D';
import Badge3D from '../components/ui/Badge3D';
import NeumorphicCard from '../components/ui/NeumorphicCard';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { AuthContext } from '../context/AuthContext';
import PerformanceTrackingService from '../services/PerformanceTrackingService';
import { screenApplicantsWithAI } from '../services/aiService';
import { checkCreditAvailability, deductCredits } from '../services/creditService';
import AIInfoTooltip from '../components/ui/AIInfoTooltip';
import AIDocumentation from '../components/ai/AIDocumentation';
import useApi from '../hooks/useApi';

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { user: authUser } = useContext(AuthContext);
  const isRecruiter = authUser?.userType === 'recruiter';
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch real data from backend
  const { data: apiData, loading, error } = useApi('applications');
  const [jobs, setJobs] = useState([]);
  
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicants, setShowApplicants] = useState(false);
  const [showAIScreening, setShowAIScreening] = useState(false);
  const [currentApplicants, setCurrentApplicants] = useState([]);
  const [aiScreeningResults, setAiScreeningResults] = useState([]);
  const [isScreening, setIsScreening] = useState(false);
  
  // Credit management state
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [requiredCredits, setRequiredCredits] = useState(0);
  const [, setCreditDeficit] = useState(0);
  const [screeningError, setScreeningError] = useState(null);
  const [lastScreeningTimestamp, setLastScreeningTimestamp] = useState(null);
  const [screeningFlags, setScreeningFlags] = useState([]);
  const [showFlagDetails, setShowFlagDetails] = useState(false);
  const [, setSelectedFlag] = useState(null);
  const [showAIDocumentation, setShowAIDocumentation] = useState(false);

  // Update jobs state when API data is received
  useEffect(() => {
    // API wraps response in { success: true, data: { jobs: [...], totalApplications: ... } }
    const jobsData = apiData?.data?.jobs || apiData?.jobs || [];
    
    if (apiData) {
      if (Array.isArray(jobsData) && jobsData.length > 0) {
        // Log and validate each job's applicants
        const validatedJobs = jobsData.map(job => {
          
          // Ensure applicants is always an array
          if (!Array.isArray(job.applicants)) {
            console.warn(`[WARNING] Job ${job.id} has invalid applicants data, converting to array`);
            job.applicants = [];
          }
          
          // Validate each applicant
          job.applicants = job.applicants.map(applicant => {
            // Ensure skills is always an array
            if (!Array.isArray(applicant.skills)) {
              applicant.skills = [];
            }
            // Ensure education is always an array
            if (!Array.isArray(applicant.education)) {
              applicant.education = [];
            }
            return applicant;
          });
          
          return job;
        });
        setJobs(validatedJobs);
      } else if (!loading) {
        setJobs([]);
      }
    }
  }, [apiData, loading, authUser]);

  // Filter jobs based on search query
  const filteredJobs = jobs.filter(job => {
    return job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           job.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
           (job.skills && job.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase())));
  });
  
  // Function to view applicants for a specific job
  const viewApplicants = (jobId) => {
    const job = jobs.find(job => job.id === jobId);
    setSelectedJob(job);
    setCurrentApplicants(job?.applicants || []);
    setShowApplicants(true);
    setShowAIScreening(false);
  };
  
  // Check if user has enough credits for AI screening
  const checkCredits = async (requiredAmount) => {
    if (!user || !user.id) {
      setScreeningError('User not authenticated');
      return false;
    }
    
    try {
      // Check if user has enough credits
      const creditCheck = await checkCreditAvailability(user.id, requiredAmount);
      
      if (!creditCheck.success) {
        setScreeningError(creditCheck.error || 'Failed to check credit availability');
        return false;
      }
      
      if (!creditCheck.data.hasEnoughCredits) {
        setShowCreditWarning(true);
        setRequiredCredits(requiredAmount);
        setCreditDeficit(creditCheck.data.deficit);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking credits:', error);
      setScreeningError('Failed to check credit availability');
      return false;
    }
  };
  
  // Function to run AI screening on applicants
  const runAIScreening = async (jobId) => {
    // Reset error state
    setScreeningError(null);
    setScreeningFlags([]);
    
    // Set selected job and update UI state
    const job = jobs.find(job => job.id === jobId);
    setSelectedJob(job);
    setShowApplicants(false);
    setShowAIScreening(true);
    
    // Get applicants for this job from the job object
    const applicantsForJob = job?.applicants || [];
    
    if (applicantsForJob.length === 0) {
      setScreeningError('No applicants found for this job');
      return;
    }
    
    // Calculate required credits (1 per applicant)
    const requiredAmount = applicantsForJob.length;
    setRequiredCredits(requiredAmount);
    
    // Check if user has enough credits
    const hasEnoughCredits = await checkCredits(requiredAmount);
    if (!hasEnoughCredits) {
      setIsScreening(false);
      return;
    }
    
    // Get the actual credit balance from the API
    const creditBalance = await checkCreditAvailability(user.id, requiredAmount);
    const actualCredits = creditBalance?.data?.currentBalance || 0;
    
    // Start screening process
    setIsScreening(true);
    setShowCreditWarning(false);
    
    try {
      // Prepare screening parameters
      const screeningParams = {
        jobId,
        applicants: applicantsForJob,
        jobDescription: job?.description || job?.responsibilities || 'No description provided',
        requirements: job?.requirements || job?.skills?.join(', ') || 'No specific requirements',
        planType: user?.subscription?.plan || 'basic',
        availableCredits: actualCredits  // Use actual credits from API
      };
      
      // Call AI service to screen applicants
      const response = await screenApplicantsWithAI(screeningParams);
      
      if (!response.success) {
        setScreeningError(response.error || 'Failed to perform AI screening');
        setIsScreening(false);
        return;
      }
      
      // Process results
      const { screenedApplicants, creditsUsed: usedCredits } = response.data;
      
      // Extract any flags from the screening results (if they exist)
      const flags = screenedApplicants
        .filter(applicant => applicant.flags && Array.isArray(applicant.flags))
        .flatMap(applicant => applicant.flags.map(flag => ({
          applicantId: applicant.id,
          applicantName: applicant.name,
          type: flag.type,
          description: flag.description,
          severity: flag.severity || 'info'
        })));
      
      setScreeningFlags(flags);
      
      // Credits are already deducted by the backend API
      // Just update the UI with the credits used info from the response
      if (usedCredits > 0) {
        setCreditsUsed(usedCredits);
      }
      
      // Track AI usage for performance metrics
      if (isRecruiter) {
        try {
          await PerformanceTrackingService.trackAIUsage({
            feature: 'ai_applicant_screening',
            creditsUsed: usedCredits
          });
        } catch (error) {
          console.error('Error tracking AI usage:', error);
        }
      }
      
      // Update results and timestamp
      setAiScreeningResults(screenedApplicants);
      setLastScreeningTimestamp(new Date().toISOString());
      
    } catch (error) {
      console.error('Error in AI screening:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack
      });
      setScreeningError(error.message || 'An unexpected error occurred during the AI screening');
    } finally {
      setIsScreening(false);
    }
  };
  
  // Function to show flag details
  const viewFlagDetails = (flag) => {
    setSelectedFlag(flag);
    setShowFlagDetails(true);
  };
  
  // Function to close flag details
  const closeFlagDetails = () => {
    setSelectedFlag(null);
    setShowFlagDetails(false);
  };
  
  // Function to go back to job list
  const backToJobs = () => {
    setSelectedJob(null);
    setShowApplicants(false);
    setShowAIScreening(false);
  };

  return (
    <div className="flex bg-gray-50 dark:bg-dark-900 min-h-screen font-sans relative">
      {/* AI Documentation Modal */}
      <AnimatePresence>
        {showAIDocumentation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAIDocumentation(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <FiCpu className="text-yellow-500" />
                  AI Features Documentation
                </h3>
                <button
                  onClick={() => setShowAIDocumentation(false)}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FiX size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <AIDocumentation feature="screening" planType={user?.subscription?.plan || 'basic'} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="fixed left-0 top-0 bottom-0 z-10">
        <Sidebar />
      </div>
      <main className="flex-1 p-8 ml-0 md:ml-24">
        <DashboardHeader />
        
        <div className="mt-8" data-component-name="ApplicationsPage">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-component-name="ApplicationsPage">
            <div>
              <GradientText variant="primary" as="h1" className="text-2xl font-bold mb-2">
                {selectedJob ? (showAIScreening ? 'AI Screening Results' : 'Job Applicants') : 'Posted Jobs'}
              </GradientText>
              <p className="text-gray-500 dark:text-gray-400">
                {selectedJob 
                  ? (showAIScreening 
                      ? `AI screening results for ${selectedJob.title}` 
                      : `Applicants for ${selectedJob.title}`) 
                  : 'View jobs you have posted and manage applicants'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {selectedJob && (
                <GradientButton
                  variant="light"
                  size="md"
                  icon={<FiFilter size={16} />}
                  className="shadow-sm border border-gray-200 dark:border-dark-700"
                  onClick={backToJobs}
                >
                  Back to Jobs
                </GradientButton>
              )}
              <GradientButton
                variant="light"
                size="md"
                icon={<FiDownload size={16} />}
                className="shadow-sm border border-gray-200 dark:border-dark-700"
              >
                Export
              </GradientButton>
            </div>
          </div>
          
          {/* Search and Filter */}
          <GlassCard variant="light" className="p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder={selectedJob ? "Search applicants by name or skills..." : "Search jobs by title, location or skills..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>
          </GlassCard>
          
          {/* Main Content */}
          {!selectedJob ? (
            // Jobs List View
            <div className="space-y-4">
              {filteredJobs.length === 0 ? (
                <div className="bg-white dark:bg-dark-800 p-8 rounded-xl text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-full">
                      <FiSearch size={32} className="text-yellow-500" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">No jobs found</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Try adjusting your search criteria
                  </p>
                </div>
              ) : (
                filteredJobs.map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card3D 
                      className="bg-white dark:bg-dark-800 p-6 rounded-xl border border-gray-100 dark:border-dark-700"
                                          >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800 dark:text-white">{job.title}</h3>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <FiMapPin size={14} />
                              <span>{job.location}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FiBriefcase size={14} />
                              <span>{job.type}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FiCalendar size={14} />
                              <span>Posted {new Date(job.postedDate).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            {job.experience && (
                              <Badge3D 
                                className="px-3 py-1 text-xs font-medium bg-yellow-500 text-white rounded-full"
                                variant="primary"
                                depth={2}
                              >
                                {job.experience}
                              </Badge3D>
                            )}
                            {job.salary && (
                              <Badge3D 
                                className="px-3 py-1 text-xs font-medium bg-green-500 text-white rounded-full"
                                variant="success"
                                depth={2}
                              >
                                {job.salary}
                              </Badge3D>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
                          <NeumorphicCard className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-dark-800 h-[42px]">
                            <div className="p-2 rounded-full bg-yellow-50 dark:bg-yellow-900/30">
                              <FiUsers className="text-yellow-500" size={16} />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Applicants</p>
                              <p className="text-base font-bold text-gray-800 dark:text-white">{job.applicantCount || job.applicants?.length || 0}</p>
                            </div>
                          </NeumorphicCard>
                          
                          <div className="flex flex-col sm:flex-row gap-2">
                            <GradientButton
                              variant="light"
                              size="md"
                              icon={<FiUsers size={16} />}
                              className="h-[42px] w-[160px]"
                              onClick={() => viewApplicants(job.id)}
                            >
                              View Applicants
                            </GradientButton>
                            
                            <GradientButton
                              variant="primary"
                              size="md"
                              icon={<FiCpu size={16} />}
                              className="h-[42px] w-[160px]"
                              onClick={() => runAIScreening(job.id)}
                            >
                              AI Screening
                            </GradientButton>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700">
                        <div className="flex flex-wrap gap-2">
                          {job.skills.map((skill, i) => (
                            <span 
                              key={i}
                              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Card3D>
                  </motion.div>
                ))
              )}
            </div>
          ) : showApplicants ? (
            // Applicants View
            <div className="space-y-4">
              {currentApplicants.length === 0 ? (
                <div className="bg-white dark:bg-dark-800 p-8 rounded-xl text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-full">
                      <FiUsers size={32} className="text-yellow-500" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">No applicants found</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    There are currently no applicants for this job posting
                  </p>
                </div>
              ) : (
                currentApplicants.map((applicant, index) => (
                  <motion.div
                    key={applicant.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card3D 
                      className="bg-white dark:bg-dark-800 p-6 rounded-xl border border-gray-100 dark:border-dark-700"
                                          >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-600 dark:text-yellow-400 font-bold text-lg">
                            {applicant.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">{applicant.name}</h3>
                            <p className="text-gray-500 dark:text-gray-400">{applicant.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                          {applicant.experience && (
                            <Badge3D 
                                className="px-3 py-1 text-xs font-medium bg-yellow-500 text-white rounded-full"
                                variant="primary"
                                depth={1}
                              >
                              {applicant.experience}
                            </Badge3D>
                          )}
                          
                          <GradientButton
                            variant="light"
                            size="sm"
                            className="shadow-sm border border-gray-200 dark:border-dark-700"
                            onClick={() => navigate(`/candidates/${applicant.candidateId || applicant.id}`)}
                          >
                            View Profile
                          </GradientButton>
                        </div>
                      </div>
                      
                      {applicant.skills && Array.isArray(applicant.skills) && applicant.skills.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700">
                          <div className="flex flex-wrap gap-2">
                            {applicant.skills.map((skill, i) => (
                              <span 
                                key={i}
                                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md"
                              >
                                {typeof skill === 'string' ? skill : JSON.stringify(skill)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card3D>
                  </motion.div>
                ))
              )}
            </div>
          ) : (
            // AI Screening View
            <div>
              {/* Credit Warning */}
              <AnimatePresence>
                {showCreditWarning && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="p-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 mt-0.5">
                          <FiAlertTriangle size={16} />
                        </div>
                        <div>
                          <p className="font-medium text-yellow-800 dark:text-yellow-300">Insufficient AI Credits</p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                            You need {requiredCredits} credits to screen all applicants, but you only have {user?.aiCredits || 0} available.
                            Please purchase additional credits to continue.
                          </p>
                          <div className="mt-3">
                            <GradientButton
                              variant="light"
                              size="sm"
                              className="border border-yellow-300 dark:border-yellow-800"
                              onClick={() => window.location.href = '/pricing'}
                            >
                              Purchase Credits
                            </GradientButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Error Message */}
              <AnimatePresence>
                {screeningError && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mt-0.5">
                          <FiAlertTriangle size={16} />
                        </div>
                        <p className="text-sm text-red-700 dark:text-red-400">{screeningError}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {isScreening ? (
                <div className="bg-white dark:bg-dark-800 p-12 rounded-xl text-center">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                        <FiCpu size={32} className="text-yellow-500 dark:text-yellow-400 animate-pulse" />
                      </div>
                      <div className="absolute inset-0 rounded-full border-4 border-t-yellow-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-3">AI Screening in Progress</h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    Our AI is analyzing resumes and matching skills to find the best candidates for {selectedJob.title}
                  </p>
                  <div className="mt-6 w-full max-w-md bg-gray-100 dark:bg-gray-700 h-2 rounded-full overflow-hidden mx-auto">
                    <div className="h-full bg-yellow-500 animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Processing {requiredCredits} applicant profiles...
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {aiScreeningResults.length === 0 ? (
                    <div className="bg-white dark:bg-dark-800 p-8 rounded-xl text-center">
                      <div className="flex justify-center mb-4">
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-full">
                          <FiUsers size={32} className="text-yellow-500 dark:text-yellow-400" />
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">No candidates found</h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        There are no candidates to screen for this job posting
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Results Header */}
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 p-4 rounded-lg mb-4">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                              <FiCpu size={20} className="text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-gray-800 dark:text-white">
                                  AI screened {aiScreeningResults.length} applicants for "{selectedJob?.title}"
                                </h3>
                                <button
                                  onClick={() => setShowAIDocumentation(true)}
                                  className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300"
                                >
                                  <FiHelpCircle size={12} />
                                  How it works
                                </button>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Sorted by match score</p>
                                {lastScreeningTimestamp && (
                                  <p className="text-xs text-gray-500 dark:text-gray-500">
                                    Screening completed {new Date(lastScreeningTimestamp).toLocaleTimeString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge3D 
                              className="px-3 py-1 text-sm font-medium rounded-full text-white flex items-center gap-1"
                              variant="primary"
                              depth={1}
                            >
                              <FiDollarSign size={14} />
                              {creditsUsed} credits used
                            </Badge3D>
                          </div>
                        </div>
                      </div>
                      
                      {/* Flags Section */}
                      {screeningFlags.length > 0 && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 p-4 rounded-lg mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <FiAlertTriangle className="text-yellow-500" size={18} />
                            <h3 className="font-medium text-yellow-700 dark:text-yellow-400">
                              {screeningFlags.length} {screeningFlags.length === 1 ? 'Flag' : 'Flags'} Detected
                            </h3>
                          </div>
                          <div className="space-y-2">
                            {screeningFlags.slice(0, 3).map((flag, index) => (
                              <div 
                                key={index} 
                                className="flex items-start gap-2 p-2 bg-white dark:bg-dark-800 rounded border border-yellow-100 dark:border-yellow-900/20"
                                onClick={() => viewFlagDetails(flag)}
                              >
                                <div className={`p-1 rounded-full mt-0.5 ${
                                  flag.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-500' :
                                  flag.severity === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-500' :
                                  'bg-blue-100 dark:bg-blue-900/30 text-blue-500'
                                }`}>
                                  <FiAlertTriangle size={12} />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                    {flag.applicantName}: {flag.type}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                    {flag.description}
                                  </p>
                                </div>
                              </div>
                            ))}
                            {screeningFlags.length > 3 && (
                              <button 
                                className="text-sm text-yellow-600 dark:text-yellow-400 font-medium hover:text-yellow-700 dark:hover:text-yellow-300"
                                onClick={() => setShowFlagDetails(true)}
                              >
                                View all {screeningFlags.length} flags
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Export Options */}
                      <div className="bg-white dark:bg-dark-800 p-4 rounded-lg shadow-sm mb-4 flex justify-between items-center">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Showing {aiScreeningResults.length} screened applicants
                        </div>
                        <div className="flex gap-2">
                          <GradientButton
                            variant="light"
                            size="sm"
                            className="border border-gray-200 dark:border-gray-700"
                            icon={<FiDownload size={14} />}
                          >
                            Export CSV
                          </GradientButton>
                        </div>
                      </div>
                      
                      {/* Applicant Cards */}
                      {aiScreeningResults.map((applicant, index) => (
                        <motion.div
                          key={applicant.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card3D 
                            className="bg-white dark:bg-dark-800 p-6 rounded-xl border border-gray-100 dark:border-gray-700"
                          >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="relative">
                                  <div className="w-14 h-14 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-600 dark:text-yellow-400 font-bold text-lg">
                                    {applicant.name.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <div className="absolute -bottom-2 -right-2 bg-white dark:bg-dark-800 rounded-full p-1 shadow-md">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                      applicant.score >= 90 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                                      applicant.score >= 80 ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                                      'bg-gradient-to-br from-yellow-500 to-amber-600'
                                    }`}>
                                      {Math.round(applicant.score / 10)}
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h3 className="text-lg font-bold text-gray-800 dark:text-white">{applicant.name}</h3>
                                  <p className="text-gray-500 dark:text-gray-400">{applicant.email}</p>
                                </div>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                                <NeumorphicCard className="px-5 py-2.5 flex items-center gap-2 h-[42px]" variant="light">
                                  <FiCpu size={16} className={applicant.score >= 90 ? 'text-green-500' : applicant.score >= 80 ? 'text-blue-500' : 'text-yellow-500'} />
                                  <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">AI Score</p>
                                    <p className={`text-base font-bold ${applicant.score >= 90 ? 'text-green-500' : applicant.score >= 80 ? 'text-blue-500' : 'text-yellow-500'}`}>{applicant.score}%</p>
                                  </div>
                                </NeumorphicCard>
                                
                                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  applicant.score >= 90 ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                                  applicant.score >= 80 ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                                  'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                                }`}>
                                  {applicant.score >= 90 ? 'Strong Match' : applicant.score >= 80 ? 'Good Match' : 'Moderate Match'}
                                </div>
                                
                                <GradientButton
                                  variant="primary"
                                  size="md"
                                  className="h-[42px] w-[160px]"
                                >
                                  View Details
                                </GradientButton>
                              </div>
                            </div>
                            
                            {/* Match Explanation */}
                            {applicant.explanation && (
                              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border border-yellow-100 dark:border-yellow-900/20">
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  <span className="font-medium">Match Explanation:</span> {applicant.explanation}
                                </p>
                              </div>
                            )}
                            
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700">
                              <div className="flex flex-wrap gap-2">
                                {Array.isArray(applicant.skills) && applicant.skills.map((skill, i) => (
                                  <span 
                                    key={i}
                                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md"
                                  >
                                    {typeof skill === 'string' ? skill : JSON.stringify(skill)}
                                  </span>
                                ))}
                              </div>
                            </div>
                            
                            {/* Flags */}
                            {applicant.flags && applicant.flags.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700">
                                <div className="flex items-center gap-2 mb-2">
                                  <FiAlertTriangle className="text-yellow-500" size={14} />
                                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Flags</p>
                                </div>
                                <div className="space-y-1">
                                  {applicant.flags.map((flag, i) => (
                                    <div 
                                      key={i} 
                                      className={`text-xs px-3 py-1.5 rounded-md ${
                                        flag.severity === 'high' ? 'bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-400' :
                                        flag.severity === 'medium' ? 'bg-yellow-50 dark:bg-yellow-900/10 text-yellow-700 dark:text-yellow-400' :
                                        'bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400'
                                      }`}
                                    >
                                      <span className="font-medium">{flag.type}:</span> {flag.description}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </Card3D>
                        </motion.div>
                      ))}
                      
                      {/* Flag Details Modal */}
                      <AnimatePresence>
                        {showFlagDetails && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                          >
                            <motion.div
                              initial={{ scale: 0.9, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.9, opacity: 0 }}
                              className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                            >
                              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                                <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                  <FiAlertTriangle className="text-yellow-500" />
                                  Screening Flags
                                </h3>
                                <button
                                  onClick={closeFlagDetails}
                                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <FiX size={20} className="text-gray-500 dark:text-gray-400" />
                                </button>
                              </div>
                              <div className="p-6 overflow-y-auto">
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                  The AI screening has identified the following potential issues or notable points about the applicants:
                                </p>
                                <div className="space-y-3">
                                  {screeningFlags.map((flag, index) => (
                                    <div 
                                      key={index} 
                                      className="p-3 bg-white dark:bg-dark-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
                                    >
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className={`p-1 rounded-full ${
                                          flag.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-500' :
                                          flag.severity === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-500' :
                                          'bg-blue-100 dark:bg-blue-900/30 text-blue-500'
                                        }`}>
                                          <FiAlertTriangle size={14} />
                                        </div>
                                        <p className="font-medium text-gray-800 dark:text-white">{flag.applicantName}</p>
                                        <div className={`px-2 py-0.5 text-xs rounded-full ${
                                          flag.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                                          flag.severity === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                                          'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                        }`}>
                                          {flag.severity} priority
                                        </div>
                                      </div>
                                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{flag.type}</p>
                                      <p className="text-sm text-gray-600 dark:text-gray-400">{flag.description}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end">
                                <GradientButton
                                  variant="primary"
                                  size="md"
                                  onClick={closeFlagDetails}
                                >
                                  Close
                                </GradientButton>
                              </div>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};


export default ApplicationsPage;
